
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { FloatingButtons } from "@/components/common/FloatingButtons";
import { Star, Quote, TrendingUp, Home, Briefcase } from "lucide-react";

const SuccessStories = () => {
  const featuredStories = [
    {
      name: "Mittal Sampat",
      location: "Mumbai, Maharashtra",
      image: "https://images.unsplash.com/photo-1494790108755-2616b9da0b7c?w=150&h=150&fit=crop&crop=face",
      rating: 5,
      story: "I was skeptical about online loan applications, but White Pearl Finance completely changed my perspective. My home loan was approved in just 2 days, and the entire process was transparent. The team was incredibly supportive, explaining every step clearly. Today, I'm living in my dream home in Bandra, and I couldn't be happier!",
      loanType: "Home Loan",
      loanAmount: "₹45 Lakhs",
      achievement: "Dream Home Achieved",
      timeline: "2 Days Approval",
      avatar: "PS",
      category: "home"
    },
    {
      name: "Rahul Upadhyay",
      location: "Delhi, NCR",
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face",
      rating: 5,
      story: "As a small business owner, getting a business loan was always a nightmare with traditional banks. White Pearl Finance made it incredibly simple. I got ₹15 lakhs in working capital within 48 hours! This funding helped me expand my textile business from Delhi to 3 more cities. My revenue has grown by 200% since then.",
      loanType: "Business Loan",
      loanAmount: "₹15 Lakhs",
      achievement: "Business Expansion",
      timeline: "48 Hours",
      avatar: "RK",
      category: "business"
    },
    {
      name: "Anita Desai",
      location: "Bangalore, Karnataka",
      image: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face",
      rating: 5,
      story: "My daughter's wedding was approaching, and I needed quick funds for the arrangements. White Pearl Finance's personal loan was approved instantly with flexible EMI options that fit my budget perfectly. The zero processing fee offer saved me ₹8,000 additionally. The wedding was grand, and my daughter was overjoyed!",
      loanType: "Personal Loan",
      loanAmount: "₹8 Lakhs",
      achievement: "Dream Wedding",
      timeline: "30 Minutes",
      avatar: "AD",
      category: "personal"
    }
  ];

  const allStories = [
    {
      name: "Vikram Singh",
      location: "Pune",
      story: "Bought my dream BMW with their car loan. Digital process was super smooth!",
      loanType: "Car Loan - ₹12 Lakhs",
      rating: 5,
      avatar: "VS"
    },
    {
      name: "Meera Patel",
      location: "Ahmedabad",
      story: "Education loan for my MBA helped me get into IIM. Great support throughout!",
      loanType: "Education Loan - ₹18 Lakhs",
      rating: 5,
      avatar: "MP"
    },
    {
      name: "Suresh Nair",
      location: "Chennai",
      story: "Gold loan approval in 15 minutes! Used it for medical emergency. Lifesaver!",
      loanType: "Gold Loan - ₹3 Lakhs",
      rating: 5,
      avatar: "SN"
    },
    {
      name: "Kavya Reddy",
      location: "Hyderabad",
      story: "Credit card with lifetime free facility. Amazing cashback and rewards!",
      loanType: "Credit Card - ₹5L Limit",
      rating: 5,
      avatar: "KR"
    },
    {
      name: "Amit Joshi",
      location: "Jaipur",
      story: "Mortgage loan against my property helped expand my restaurant chain.",
      loanType: "Mortgage Loan - ₹25 Lakhs",
      rating: 5,
      avatar: "AJ"
    },
    {
      name: "Ritu Agarwal",
      location: "Lucknow",
      story: "Balance transfer saved me ₹2 lakhs in interest. Best financial decision!",
      loanType: "Home Loan Transfer - ₹35 Lakhs",
      rating: 5,
      avatar: "RA"
    }
  ];

  const stats = [
    { number: "150+", label: "Happy Customers", icon: Star },
    { number: "₹100 Cr+", label: "Loans Disbursed", icon: TrendingUp },
    { number: "98%", label: "Approval Rate", icon: Home },
    { number: "4.8★", label: "Customer Rating", icon: Quote }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 pt-20">
      {/* Hero Section */}
      <section className="py-16 px-4">
        <div className="container mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Success Stories
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
            Real stories from real customers who achieved their dreams with White Pearl Finance
          </p>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-12 max-w-4xl mx-auto">
            {stats.map((stat, index) => (
              <div key={index} className="text-center p-4 bg-white/10 backdrop-blur-lg rounded-xl border border-white/20">
                <stat.icon className="h-8 w-8 text-blue-400 mx-auto mb-2" />
                <div className="text-3xl font-bold text-white">{stat.number}</div>
                <div className="text-gray-300">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Success Stories */}
      <section className="py-16 px-4">
        <div className="container mx-auto">
          <h2 className="text-4xl font-bold text-white text-center mb-12">Featured Success Stories</h2>

          <div className="space-y-12">
            {featuredStories.map((story, index) => (
              <Card key={index} className="bg-white/10 backdrop-blur-lg border-white/20 hover:bg-white/15 transition-all duration-300 overflow-hidden">
                <CardContent className="p-8">
                  <div className="grid lg:grid-cols-3 gap-8 items-center">
                    {/* Story Content */}
                    <div className="lg:col-span-2">
                      <div className="flex items-start mb-6">
                        <Quote className="h-8 w-8 text-blue-400 mr-4 flex-shrink-0 mt-1" />
                        <div>
                          <p className="text-gray-200 text-lg leading-relaxed mb-4">
                            "{story.story}"
                          </p>

                          <div className="flex items-center mb-4">
                            <Avatar className="h-16 w-16 mr-4">
                              <AvatarImage src={story.image} alt={story.name} />
                              <AvatarFallback className="bg-blue-500 text-white text-lg">{story.avatar}</AvatarFallback>
                            </Avatar>
                            <div>
                              <h3 className="text-xl font-semibold text-white">{story.name}</h3>
                              <p className="text-gray-400">{story.location}</p>
                              <div className="flex mt-1">
                                {[...Array(story.rating)].map((_, i) => (
                                  <Star key={i} className="h-4 w-4 text-yellow-400 fill-current" />
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Loan Details */}
                    <div className="space-y-4">
                      <div className="bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-xl p-6 border border-white/10">
                        <div className="text-center mb-4">
                          {story.category === 'home' && <Home className="h-8 w-8 text-blue-400 mx-auto mb-2" />}
                          {story.category === 'business' && <Briefcase className="h-8 w-8 text-green-400 mx-auto mb-2" />}
                          {story.category === 'personal' && <Star className="h-8 w-8 text-purple-400 mx-auto mb-2" />}
                        </div>

                        <div className="space-y-3 text-center">
                          <div>
                            <div className="text-2xl font-bold text-white">{story.loanAmount}</div>
                            <div className="text-blue-400 font-semibold">{story.loanType}</div>
                          </div>

                          <div className="border-t border-white/20 pt-3">
                            <Badge className="bg-green-500/20 text-green-400 border-green-500/30 mb-2">
                              {story.achievement}
                            </Badge>
                            <div className="text-sm text-gray-300">
                              Approved in {story.timeline}
                            </div>
                          </div>
                        </div>
                      </div>

                      <Button className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700">
                        Apply for Similar Loan
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* More Success Stories */}
      <section className="py-16 px-4">
        <div className="container mx-auto">
          <h2 className="text-4xl font-bold text-white text-center mb-12">More Success Stories</h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {allStories.map((story, index) => (
              <Card key={index} className="bg-white/10 backdrop-blur-lg border-white/20 hover:bg-white/15 transition-all duration-300 hover:scale-105">
                <CardContent className="p-6">
                  <div className="flex items-center mb-4">
                    <Avatar className="h-12 w-12 mr-3">
                      <AvatarFallback className="bg-blue-500 text-white">{story.avatar}</AvatarFallback>
                    </Avatar>
                    <div>
                      <h3 className="font-semibold text-white">{story.name}</h3>
                      <p className="text-sm text-gray-400">{story.location}</p>
                    </div>
                  </div>

                  <div className="flex mb-3">
                    {[...Array(story.rating)].map((_, i) => (
                      <Star key={i} className="h-4 w-4 text-yellow-400 fill-current" />
                    ))}
                  </div>

                  <p className="text-gray-200 mb-4 text-sm leading-relaxed">
                    "{story.story}"
                  </p>

                  <div className="bg-blue-500/20 rounded-lg p-3">
                    <p className="text-blue-400 font-semibold text-sm">{story.loanType}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 px-4">
        <div className="container mx-auto">
          <Card className="bg-gradient-to-r from-green-500/20 to-blue-500/20 backdrop-blur-lg border-white/20">
            <CardContent className="p-12 text-center">
              <h2 className="text-4xl font-bold text-white mb-4">Your Success Story Awaits</h2>
              <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
                Join thousands of satisfied customers who achieved their dreams with White Pearl Finance. Your story could be next!
              </p>
              <div className="flex justify-center space-x-6 mb-8">
                <div className="text-center">
                  <div className="text-3xl font-bold text-green-400">48 Hours</div>
                  <div className="text-gray-300">Average Approval</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-400">₹100+ Cr</div>
                  <div className="text-gray-300">Maximum Loan</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-purple-400">upto 8.40%</div>
                  <div className="text-gray-300">Lowest Rate*</div>
                </div>
              </div>
              <Button size="lg" className="bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 text-lg px-8 py-4 h-auto">
                Start Your Success Story
              </Button>
            </CardContent>
          </Card>
        </div>
      </section>

      <FloatingButtons />
    </div>
  );
};

export default SuccessStories;
